/**
 * AccountBulkBar - Toolbar shown when accounts are selected.
 * Provides Export Emails and Export Data actions.
 */
import { Mail, X } from "lucide-react";
import { cn } from "../lib/utils";

interface AccountBulkBarProps {
  count: number;
  totalCount: number;
  onSelectAll: () => void;
  onExportEmails: () => void;
  onClear: () => void;
  exporting?: boolean;
}

export function AccountBulkBar({
  count,
  totalCount,
  onSelectAll,
  onExportEmails,
  onClear,
  exporting = false,
}: AccountBulkBarProps) {
  const allSelected = count === totalCount && totalCount > 0;

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-xl border px-3 py-2",
        "bg-brand/5 border-brand/20"
      )}
      data-testid="account-bulk-bar"
    >
      <span className="text-xs font-medium text-brand">
        {count} selected
      </span>

      {!allSelected && (
        <button
          type="button"
          onClick={onSelectAll}
          className="text-xs text-brand underline hover:text-brand/80"
          data-testid="bulk-select-all"
        >
          Select all {totalCount}
        </button>
      )}

      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={onExportEmails}
          disabled={exporting}
          className={cn(
            "flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium",
            "bg-brand text-white transition-colors",
            exporting ? "opacity-50 cursor-not-allowed" : "hover:bg-brand/90 active:bg-brand/80"
          )}
          data-testid="bulk-export-emails"
        >
          <Mail className="h-3.5 w-3.5" />
          {exporting ? "Exporting..." : "Export Emails"}
        </button>

        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs text-text-muted hover:text-text-primary"
          data-testid="bulk-clear"
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </button>
      </div>
    </div>
  );
}
